// material ui
import { Grid, Typography } from '@material-ui/core';
import { MTableToolbar } from 'material-table';
import React, { ReactElement } from 'react';

// api
import { ResourceActiveDisabled, ResourceActiveEnabled } from '~/src/dash/api/console-api';
import {
  deleteNetwork,
  getNetworks,
  INetwork,
  networkStreamTypeToString,
  transformNetworkWrapper,
  updateNetwork,
} from '~/src/dash/api/network';
import { IHttpResponse } from '~/src/dash/api/rest';
// components
import { IListTableColumn, INetworkRowData, ListTable } from '~/src/dash/components/list-table';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
// other
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteNetworkSettingsClone,
  RouteNetworkSettingsExisting,
  RouteNetworkSettingsNew,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { networkStreamTypeToChip } from '~/src/dash/utils/network-status';
import { IPermissionCheck, IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { checkUserInfoMasked, hasRole, ROLE_SUPER } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { isOtteraResellerAdmin } from '~/src/dash/utils/ui';
// views
import { RowButton } from '~/src/dash/views/component-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

// NOTE: Use ListTable to render the a resource list. When updating a single field (like active or tier)

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  // list resource
  networks: INetwork[];
  loading: boolean;
  updating: boolean;
  // permissions
  readAuthorization: IPermissionCheck;
  writeAuthorization: IPermissionCheck;
  // Snackbar messages
  snackbarMessage: string;
  snackbarError: boolean;
}
export const NetworkListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      networks: [] as INetwork[],
      loading: true,
      updating: false,
      readAuthorization: IS_AUTHORIZED(['read_network'], this.props.user.user),
      writeAuthorization: IS_AUTHORIZED(['write_network'], this.props.user.user),
      snackbarMessage: '',
      snackbarError: false,
    };

    // NOTE: We should try to build helper functions for componentDidMount, to keep the process clean
    // and readable, especially when we add more steps
    componentDidMount() {
      this.getNetworks();
    }

    // NOTE: In general, like we do with props and state, we should try to organize the functions
    // within a view/component to keep the code clean.  In addition, we should try to name the functions
    // as well as possible to show what is happening and if more explanation is needed, use a comment
    // above the function definition

    // get networks via api call, transform them and set state
    getNetworks(): void {
      getNetworks(this.props.session.publisher.ID)
        .then((val: IHttpResponse<INetwork[]>) => {
          const networks = [] as INetwork[];
          if (val.parsedBody) {
            for (const item of val.parsedBody) {
              const network = transformNetworkWrapper(item);
              networks.push(network);
            }
          }
          this.setState({
            networks,
            loading: false,
          });
        })
        .catch(() => {
          this.setState({
            networks: [] as INetwork[],
            snackbarError: true,
            snackbarMessage: 'Error while getting the list of networks from the API.',
            loading: false,
          });
        });
    }

    // ----- NAVIGATION FUNCTIONS ----- //

    // navigates to existing network setting form
    viewNetworkSettingsByID = (id: string): void => {
      navigate(RouteNetworkSettingsExisting, extractRouteParams(this.props), {
        net: id,
      });
    };

    // navigates to clone existing network setting form
    cloneNetworkSettingsById = (id: string): void => {
      navigate(RouteNetworkSettingsClone, extractRouteParams(this.props), {
        net: id,
      });
    };

    // navigates to new network setting form
    createNetwork = (): void => {
      if (this.state.updating) {
        return;
      }
      navigate(
        RouteNetworkSettingsNew,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    getItemSettingRoute = (id: string): string => {
      return getRoutePath(RouteNetworkSettingsExisting, extractRouteParams(this.props), {
        net: id,
      });
    };

    // ----- COMPONENT HELPER  FUNCTIONS ----- //

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    // NOTE: use a function to determine superUser or rights if possible to ensure consistency
    isSuperUser = (): boolean => {
      return hasRole(this.props.user.user, [ROLE_SUPER]);
    };

    // ----- HANDLER HELPER FUNCTIONS FOR LIST TABLE ----- //

    handleSetUpdating = (updating: boolean) => {
      this.setState({
        updating,
      });
    };

    // NOTE: functions passed into a child component which update state in the parent
    // should be called handleFunc.  Handlers are used to not directly modify the props/state
    // and ensure that the component is properly re-rendered

    // deletes the network and gives user msg if success/failure, returns a promise
    // for ModelDialogDeleteView which requires a promise to close dialog
    handleDeleteNetwork = (networkID: string) => {
      this.setState(
        {
          updating: true,
        },
        () => {
          deleteNetwork(networkID, this.props.session.publisher.ID)
            .then(() => {
              const networks = this.state.networks.filter(
                (network: INetwork) => network.ID.toString() !== networkID,
              );
              this.setState({
                networks,
                snackbarMessage: 'Successfully deleted network',
                snackbarError: false,
                updating: false,
              });
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error while deleting network',
                snackbarError: true,
                updating: false,
              });
            });
        },
      );
      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    // handles change of network active/inactive and updates the network

    // Note: when updating the REST API's for other resources, see UpdateNetwork / UpdateNetworkHandler
    // which can either update selected fields OR the entire object/associations
    // This is useful since a full object update requires the full object which at times
    // (example: in the bidder list, the bidder object is not completely populated) the resource
    // is only partial and/or we only want to update a single field.
    handleChangeActive = (itemID: string, checked: boolean): void => {
      if (this.state.updating) {
        return;
      }
      const networks = [...this.state.networks];
      let selectedIndex = -1;
      // find the network to be updated
      for (let i = 0; i < networks.length; i++) {
        if (networks[i].ID.toString() === itemID) {
          networks[i].Active = checked ? ResourceActiveEnabled : ResourceActiveDisabled;
          selectedIndex = i;
          break;
        }
      }
      // create a network with the fields that need to be updated as well as the ID
      if (selectedIndex >= 0) {
        const network = {
          ID: networks[selectedIndex].ID,
          Active: networks[selectedIndex].Active,
        } as INetwork;
        this.setState(
          {
            updating: true,
          },
          () => {
            // this is not full update (false) since we only want to update the active field
            updateNetwork(network, this.props.session.publisher.ID, false)
              .then(() => {
                this.setState({
                  networks,
                  snackbarMessage: 'Successfully saved network.',
                  snackbarError: false,
                  updating: false,
                });
              })
              .catch(() => {
                this.setState({
                  snackbarMessage: 'Failed to save network.',
                  snackbarError: true,
                  updating: false,
                });
              });
          },
        );
      }
    };

    // ----- RENDER HELPER FUNCTIONS ----- //

    renderStreamTypeChip = (network: INetwork): ReactElement => {
      const ChipComponent = networkStreamTypeToChip(network.StreamType);
      if (ChipComponent === undefined) {
        return <Typography>Unknown</Typography>;
      }
      return (
        <ChipComponent
          onClick={() => this.viewNetworkSettingsByID(network.ID.toString())}
          label={networkStreamTypeToString(network.StreamType)}
        />
      );
    };

    // renders the custom tool bar for the main list
    renderMainListToolbar = (props: any) => {
      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, marginLeft: '-48px', width: '300px' },
      };
      const { user } = this.props;
      const writeAuthorization = IS_AUTHORIZED(['write_network'], user.user);

      return (
        <Grid item>
          <Grid container>
            <Grid
              item
              style={{
                marginLeft: -10,
                width: '100%',
                marginTop: 15,
              }}
            >
              <Grid container direction='row' justify='space-between' alignItems='center'>
                {/* search bar - Grid with class name "MTableToolbarWrapper" is necessary for auto focusing search bar on load */}
                <Grid item className='MTableToolbarWrapper'>
                  <MTableToolbar {...copyProps} />
                </Grid>
                {/* Upload Mediafiles Button */}
                <Grid item style={{ paddingTop: 25, paddingBottom: 40 }}>
                  {!writeAuthorization.isAuthorized ? (
                    <ModelMediumButton
                      variant='contained'
                      onClick={this.createNetwork}
                      disabled={true}
                    >
                      + New Network
                    </ModelMediumButton>
                  ) : (
                    <RowButton
                      right={
                        <ModelMediumButton
                          variant='contained'
                          color='primary'
                          style={{ paddingLeft: 15, paddingRight: 15 }}
                          onClick={this.createNetwork}
                        >
                          + New Network
                        </ModelMediumButton>
                      }
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    render() {
      // NOTE: We destructure props and states used in render functions to clearly
      // show what we are passing as well as keeping the code clean and easier to read

      const {
        networks,
        loading,
        snackbarError,
        snackbarMessage,
        readAuthorization,
        writeAuthorization,
        updating,
      } = this.state;
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      if (networks.length === 0 && loading) {
        return <LoadingView active={true} text='Loading the network settings' />;
      }

      if (!readAuthorization.isAuthorized && !writeAuthorization.isAuthorized) {
        return (
          <ModelMissingPermission
            missingPermissions={[
              ...readAuthorization.missingPermissions,
              ...writeAuthorization.missingPermissions,
            ]}
          />
        );
      }
      // column information for material table => 'field' should match name in state,
      // 'title' for column display in table, 'renderTypes' can be found in list-table.ts. If the 'renderType' doesn't exist
      // there yet, create a new 'renderType' if it can be reused or if it seems unique, set a custom 'render'
      // in the 'render' field.  Additional column props can be found at https://material-table.com/#/docs/get-started
      const columns: IListTableColumn[] = [
        { field: 'Active', title: 'Active', renderType: 'activeswitch', searchable: false },
        { field: 'Name', title: 'Network Name', renderType: 'namelink' },
        // OVERUNDER requires two fields, field2 is under
        {
          field: 'Network',
          title: 'Stream Type',
          render: (rowData: any) => this.renderStreamTypeChip(rowData.Network),
          searchable: false,
        },
        {
          field: 'InventorySplit',
          title: 'Inventory Split',
          renderType: 'bignumber',
          searchable: false,
        },
        { field: 'Actions', title: 'Actions', renderType: 'deleteclone', searchable: false },
      ];
      const superUserAndResellerAdminColumns: IListTableColumn[] = [
        { field: 'Active', title: 'Active', renderType: 'activeswitch', searchable: false },
        { field: 'IDName', title: 'Network Name', renderType: 'namelink' },
        // OVERUNDER requires two fields, field2 is under
        { field: 'UUID', title: 'UUID *', renderType: 'copy' },
        {
          field: 'Network',
          title: 'Stream Type',
          render: (rowData: any) => this.renderStreamTypeChip(rowData.Network),
          searchable: false,
        },
        {
          field: 'InventorySplit',
          title: 'Inventory Split',
          renderType: 'bignumber',
          searchable: false,
        },
        { field: 'Actions', title: 'Actions', renderType: 'deleteclone', searchable: false },
      ];
      // generate row data, the object keys here need to match the fields above
      const data: INetworkRowData[] = networks.map((network: INetwork) => {
        return {
          // For active field, use boolean since all lists will likely have an active field and the typings may vary
          Active: network.Active === ResourceActiveEnabled,
          Name: checkUserInfoMasked(this.props.user.user)
            ? `demo_network_${network.ID}`
            : network.Name,
          ID: network.ID.toString(),
          IDName: `[${network.ID.toString()}] ${network.Name}`,
          UUID: network.UUID,
          Network: network,
          InventorySplit: network.Channels.length,
        };
      });

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            <ListTable
              title={''}
              columns={
                this.isSuperUser() || isOtteraResellerAdmin(user.user, session.publisher)
                  ? superUserAndResellerAdminColumns
                  : columns
              }
              data={data}
              isLoading={loading}
              isUpdating={updating}
              handleChangeActive={this.handleChangeActive}
              getItemSettingRoute={this.getItemSettingRoute}
              handleDeleteItem={this.handleDeleteNetwork}
              handleCloneItem={this.cloneNetworkSettingsById}
              handleSetUpdating={this.handleSetUpdating}
              renderCustomToolbar={this.renderMainListToolbar}
              pageSize={10}
              readRoleName={'read_network'}
              writeRoleName={'write_network'}
            />
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
